/* @flow */
import * as React from 'react';

import get from 'lodash/get';
import { matchPath } from 'react-router-dom';

import type { TranslatorProps } from '@gandi/translate';
import { Spinner, Base, Alert } from '@gandi/ui';
import { Divider, Action, ListRow } from '@gandi/ui/deprecated';

import { Intro } from '../../../Layout/components/Intro';
import type {
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
  ResourceThunkAction,
} from '../../../types';
import {
  instanceRenew,
  loadInstance,
  loadCouponsIfRequired,
  type DryRunError,
} from '../module';

type Params = { identifier: string, cartId: string };

export type SimpleHostingRenewPresenterProps = {
  translator: TranslatorProps,

  adminSHSURL: string,
  cartOverviewUrl: string,

  identifier: string,
  // eslint-disable-next-line react/no-unused-prop-types
  cartId: ?string,

  // eslint-disable-next-line react/no-unused-prop-types
  setCartUUID: any,
  // eslint-disable-next-line react/no-unused-prop-types
  getCart: ResourceThunkAction,
  instanceRenew: typeof instanceRenew,
  isTrial: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  loadInstance: typeof loadInstance,
  // eslint-disable-next-line react/no-unused-prop-types
  loadCouponsIfRequired: typeof loadCouponsIfRequired,

  ...WithRouterProps<Params>,
  ...
};

type ComponentState = {
  error: ?DryRunError,
  ...
};

class SimpleHostingRenewPresenter extends React.Component<
  SimpleHostingRenewPresenterProps,
  ComponentState,
> {
  static defaultProps: { ... } = {};

  static preFetch({
    dispatch,
    params,
  }: PreFetchArgs<{}, Params>): PreFetchReturn<> {
    return Promise.allSettled([
      dispatch(loadInstance(params.identifier)),
      dispatch(loadCouponsIfRequired()),
    ]);
  }

  constructor(props: SimpleHostingRenewPresenterProps) {
    super(props);
    // eslint-disable-next-line react/state-in-constructor
    this.state = {
      error: undefined,
    };
  }

  componentDidMount() {
    const { navigate, identifier, translator, location } = this.props;

    this.props
      .instanceRenew(translator, identifier)
      // $FlowFixMe[prop-missing] - to be rechecked
      .then(({ cartId, error }: { cartId: string, error: ?any }) => {
        if (
          cartId &&
          // Detect if the path is active
          // This condition is a bit weird, the scenario needs to be checked: when does url may not match?
          // migration note: similar to `useMatch` implementation
          !matchPath(
            `/simplehosting/renew/${identifier}/${cartId}`,
            location.pathname,
          )
        ) {
          navigate(
            {
              pathname: `/simplehosting/renew/${identifier}/${cartId}`,
              search: this.props.searchParams.toString(),
            },
            { replace: true },
          );
        }

        if (error && 'response' in error && error.response.status !== 201) {
          // the product was not added to the cart.
          // we should display the error in this screen, so let's reject to
          // prevent redirection to cart overview.
          return Promise.reject(error);
        }

        return navigate(`/cart/${cartId}`, { replace: true });
      })
      .catch(err => {
        this.setState({ error: err });
      });
  }

  decorateContent(...content: Array<React.Node>): React.MixedElement {
    return <div>{React.Children.toArray(content)}</div>;
  }

  getErrorMessage(): null | string {
    if (!this.state.error) return null;

    const { __ } = this.props.translator;
    const errors = 'errors' in this.state.error ? this.state.error.errors : {};

    if ('_error' in errors) return errors._error;

    const error = this.state.error;
    return get(
      error,
      'response.data.message',
      __(
        'An unexpected error occurred. ' +
          'Please refresh the page or try again later.',
      ),
    );
  }

  renderError(): null | React.Node {
    if (!this.state.error) return null;

    const { __ } = this.props.translator;

    return (
      <Base marginBottom="base">
        <Alert message={this.getErrorMessage()} intent="danger" />
        <ListRow separatorBottom>
          <Divider direction="columnToRow" spread="equal">
            <Action
              rel="external"
              href={this.props.adminSHSURL}
              key="dashboard"
              variant="block"
              icon="simplehosting"
            >
              {__('Back to dashboard')}
            </Action>
            <Action
              rel="external"
              to={this.props.cartOverviewUrl}
              key="cart-overview"
              variant="block"
              icon="cart"
            >
              {__('Go to your cart')}
            </Action>
          </Divider>
        </ListRow>
      </Base>
    );
  }

  renderIntro(): React.Node {
    const isTrial = this.props.isTrial;
    const __ = this.props.translator.__;

    return (
      <Intro
        icon="simplehosting"
        title={isTrial ? __('Buy trial instance') : __('Renew an instance')}
      />
    );
  }

  /**
   * Render component
   * @return {ReactElement}
   */
  render(): React.MixedElement {
    if (this.state.error) {
      return this.decorateContent(this.renderIntro(), this.renderError());
    }

    return this.decorateContent(this.renderIntro(), <Spinner centered />);
  }
}

export default SimpleHostingRenewPresenter;
